import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import type { UseProductionByDepartmentResult } from '@hooks/dashboard/useProductionByDepartment'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  buildComparisonFooter,
  formatCurrency,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { FinanceMetricTileItem } from '../../FinanceMetricTiles.component'
import { ComparisonMetricFooter } from '../shared/dashboardFinanceMetricFooter'

type ProductionByDepartmentCardProps = {
  productionByDepartmentHref: string
  productionPerHygieneVisitHref: string
  range: RangeKey
  data: Pick<
    UseProductionByDepartmentResult,
    | 'dentistMetric'
    | 'hygienistMetric'
    | 'productionPerVisitMetric'
    | 'loading'
    | 'hasPayload'
    | 'hasDentistHygienistData'
    | 'hasProductionPerVisitData'
  >
  isAllView?: boolean
}

function comparisonMetricFooter(
  metric:
    | { current: number; previous: number; percentChange: number }
    | undefined,
): React.ReactNode {
  if (!metric) return undefined
  return (
    <ComparisonMetricFooter
      {...buildComparisonFooter(metric, { isCurrency: true })}
    />
  )
}

export function ProductionByDepartmentCard({
  productionByDepartmentHref,
  productionPerHygieneVisitHref,
  data,
  isAllView = false,
}: ProductionByDepartmentCardProps): React.ReactElement {
  const {
    dentistMetric,
    hygienistMetric,
    productionPerVisitMetric,
    loading,
    hasPayload,
    hasDentistHygienistData,
    hasProductionPerVisitData,
  } = data
  const isDepartmentEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasDentistHygienistData,
  )
  const isProductionPerVisitEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasProductionPerVisitData,
  )
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <div
      className={`dashboard-finance-production-dept-column${
        isAllView ? ' dashboard-finance-production-dept-column--all' : ''
      }`.trim()}
    >
      <div className="dashboard-finance-production-dept-left">
        <DashboardSectionCard
          title="Production by department"
          titleHref={productionByDepartmentHref}
          className="dashboard-finance-production-dept-card"
          loading={pending}
          skeleton={{ metrics: 2, block: 'none' }}
          isEmpty={isDepartmentEmpty}
        >
          <div className="dashboard-finance-production-dept-rows">
            <FinanceMetricTileItem
              leftLabel="Dentists"
              value={
                dentistMetric
                  ? formatCurrency(dentistMetric.current, { compact: true })
                  : HYGIENE_PLACEHOLDER
              }
              footer={comparisonMetricFooter(dentistMetric)}
            />
            <FinanceMetricTileItem
              leftLabel="Hygienists"
              value={
                hygienistMetric
                  ? formatCurrency(hygienistMetric.current, { compact: true })
                  : HYGIENE_PLACEHOLDER
              }
              footer={comparisonMetricFooter(hygienistMetric)}
            />
          </div>
        </DashboardSectionCard>
      </div>
      {!isAllView ? (
        <div className="dashboard-finance-production-dept-right">
          <DashboardSectionCard
            title="Prod. per visit"
            titleHref={productionPerHygieneVisitHref}
            className="dashboard-finance-production-dept-small-card"
            loading={pending}
            skeleton={{ metrics: 1, block: 'none' }}
            isEmpty={isProductionPerVisitEmpty}
          >
            <FinanceMetricTileItem
              leftLabel="Prod. per visit"
              value={
                productionPerVisitMetric
                  ? formatCurrency(productionPerVisitMetric.current)
                  : HYGIENE_PLACEHOLDER
              }
              footer={comparisonMetricFooter(productionPerVisitMetric)}
            />
          </DashboardSectionCard>
        </div>
      ) : null}
    </div>
  )
}
